// ================================================================================================
import { ReactNode, Component, CSSProperties, ReactElement, Children } from 'react';
import ReactDOM from 'react-dom';
import EventListener from 'react-event-listener';
import _ from "lodash";
import { isDefined, isFunction, isNumeric, isString } from "@Utils/Micro";
import { getZDepthShadow } from "@Utils/Colors";
import { BoardTheme, BoardTheming } from '@UI/Dashboard/BoardTheming';
import { screenWidth, screenHeight } from '@Utils/Utils';
import "./ExDialog.less";
import { ColorParse } from '@Utils/ColorParser';
import Toolbar from '@Primitive/Toolbar';
import { Props } from '@Utils/ReactProps';
// ===================================================================================================
interface ExDialogProps extends Props<ExDialog> {
    Enable: boolean;
    Conf: DialogConf;
    style?: CSSProperties;
    toolbarStyle?: CSSProperties;
}
// ---------------------------------------------------------------------------------------------------
export interface DialogConf {
    /**
     * Title
     */
    title?: ReactNode;

    /**
     * Static React Node Body
     */
    body?: ReactNode;

    /**
     * Dynamic react node body.
     * The function will be call every time the dialog is refresh or resized.
     *
     * The width and height is valid only when we are using static content size
     */
    bodyFunc?: (width: number, height: number, themeContext?: BoardTheme) => ReactNode

    /**
     * Array of buttons
     */
    actions?: ReactElement<any>[];

    /**
     * Dynamic make of buttons
     */
    actionsFunc?: (themeContext?: BoardTheme) => ReactElement<any>[];

    /**
     * CB for handling the close when the user click outside of dialog
     */
    handleClose?: () => void;

    /**
     * Static size for the body based on percentage [0-1]
     */
    contentStaticSize?: number | { w: number, h: number };
}
// ---------------------------------------------------------------------------------------------------
export class ExDialog extends Component<ExDialogProps> {
    constructor(props: ExDialogProps) {
        super(props);
    }
    // -----------------------------------------------------------------------------------------------
    requestClose = () => {
        if (this.props.Conf.handleClose)
            this.props.Conf.handleClose();
    };
    // -----------------------------------------------------------------------------------------------
    handleKeyUp = event => {
        if (event?.which == 27 ||
            event?.keyCode == 27 ||
            event?.key == 'Escape' ||
            event?.key == 'Esc')
            this.requestClose();
    };
    // -----------------------------------------------------------------------------------------------
    handleResize = () => {
        this.forceUpdate();
    };
    // -----------------------------------------------------------------------------------------------
    renderLayer = () => {
        const theme = BoardTheming.GetActiveTheme();

        let width: number | string = "fit-content";
        let height = undefined;
        let minWidth: number | string = "20vw";
        let minHeight: number | string = "10vw";
        let sHeight = screenHeight();
        let sWidth = screenWidth();

        let maxWidth = Math.floor(sWidth * 0.97);
        let maxHeight = Math.floor(sHeight * 0.97);

        let topPage = Math.floor((sHeight - maxHeight) / 2);

        if (isDefined(this.props.Conf.contentStaticSize)) {
            let wScale = 1;
            let hScale = 1;
            if (isNumeric(this.props.Conf.contentStaticSize)) {
                wScale = this.props.Conf.contentStaticSize as number;
                hScale = this.props.Conf.contentStaticSize as number;
            } else {
                wScale = (this.props.Conf.contentStaticSize as { w: number, h: number }).w;
                hScale = (this.props.Conf.contentStaticSize as { w: number, h: number }).h;
            }

            width = Math.floor(sWidth * wScale);
            minWidth = width;
            maxWidth = width;
            height = Math.floor(sHeight * hScale);
            minHeight = height;
            maxHeight = height;
        }

        const zIndex = {
            dialogOverlay: 1400,
            dialog: 1500
        };

        /* calculate max height */
        let pageMaxHeight = maxHeight;
        const toolbarHeight = theme.dialog.height;

        /* calculate action container */
        let actionsContainer = null;
        let pagePaddingBottom = 0;
        let useToolbar = isString(this.props.Conf.title);
        let toolbarActionsContainer = [];
        let actions = this.props.Conf.actions;
        if (isDefined(this.props.Conf.actionsFunc))
            actions = this.props.Conf.actionsFunc(theme);

        if (!useToolbar && Children.count(actions) > 0) {
            actionsContainer = <div style={{
                boxSizing: 'border-box',
                padding: 5,
                width: "100%",
                textAlign: 'right',
                marginTop: -1,
                height: toolbarHeight,
                bottom: 0,
                position: "absolute",
                backgroundColor: theme.dialog.toolbarBackgroundColor,
                color: theme.dialog.fontColor,
                display: 'flex',
                flexDirection: 'row-reverse',
                alignItems: 'center'
            }}>
                {Children.toArray(actions)}
            </div>;

            /* allocate bottom room for the actions buttons */
            pagePaddingBottom = toolbarHeight;

            /* remove from page max the actions */
            pageMaxHeight -= toolbarHeight;
        } else if (useToolbar) {
            toolbarActionsContainer = actions;

            /* remove from page max the title */
            pageMaxHeight -= toolbarHeight;
        }

        let bodyColor = theme.dialog.backgroundColor;
        let boxShadow = undefined;
        if (isDefined(this.props.style) && isDefined(this.props.style.backgroundColor)) {
            let canvasColor = ColorParse(this.props.style.backgroundColor);
            if (canvasColor.alpha <= 0.01) {
                bodyColor = "transparent"
                boxShadow = "none";
            }
        }

        /* Body render size */
        let bodyWidth = -1;
        let bodyHeight = -1;
        if (isDefined(this.props.Conf.contentStaticSize)) {
            bodyWidth = width as number;
            bodyHeight = pageMaxHeight;
        }

        return <div
            style={{
                position: 'fixed',
                boxSizing: 'border-box',
                zIndex: zIndex.dialog,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                boxShadow: boxShadow
            }}>
            <EventListener
                target="window"
                onKeyUp={this.handleKeyUp}
                onResize={this.handleResize}
            />
            <div
                className="exdialog-dialog"
                style={{
                    width,
                    height,
                    minHeight,
                    minWidth,
                    maxWidth,
                    maxHeight,
                    top: topPage,
                    zIndex: zIndex.dialog,
                    paddingBottom: pagePaddingBottom,
                    borderRadius: theme.dialog.borderRadius,
                    boxShadow: boxShadow,
                    ...this.props.style
                }}>
                {useToolbar
                    ? <Toolbar
                        title={this.props.Conf.title as string}
                        style={{
                            zIndex: 1000,
                            position: 'relative',
                            height: toolbarHeight,
                            boxShadow: getZDepthShadow(theme.dialog.shadowLevel),
                            color: theme.dialog.toolbarFontColor,
                            backgroundColor: theme.dialog.toolbarBackgroundColor,
                            ...this.props.toolbarStyle
                        }}
                        rightItems={toolbarActionsContainer} />
                    : null}
                <div id="body"
                    style={{
                        maxHeight: pageMaxHeight,
                        backgroundColor: bodyColor
                    }}>
                    {this.props.Conf.body}
                    {isFunction(this.props.Conf.bodyFunc) ? this.props.Conf.bodyFunc(bodyWidth, bodyHeight, theme) : null}
                </div>
                {actionsContainer}
            </div>
            <div
                style={{
                    top: 0,
                    left: 0,
                    opacity: 1,
                    zIndex: zIndex.dialogOverlay,
                    position: 'fixed',
                    height: '100%',
                    width: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.54)',
                    transform: 'translateZ(0px)',
                    transition: 'left 0ms cubic-bezier(0.23, 1, 0.32, 1) 0ms, opacity 400ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
                    WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
                    willChange: 'opacity'
                }}
                onClick={this.requestClose} />
        </div>;
    }
    // --------------------------------------------------------------------------------------------
    render() {
        return this.props.Enable ? ReactDOM.createPortal(this.renderLayer(), document.body) : null;
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------